import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-teams',
  version: '2.0.0',
  properties: [
    {
      key: 'useCredential',
      label: 'Authentication',
      type: 'boolean',
      default: true,
      description: 'Use stored OAuth2 credential (user permissions) or M2M authentication (app permissions)'
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'microsoft_teams_oauth',
      },
      displayOptions: {
        show: {
          useCredential: [true]
        }
      },
      description: 'Select Microsoft Teams OAuth2 credential'
    },
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'sendMessage',
      options: [
        { value: 'sendMessage', name: 'Send Message', description: 'Send a message to a channel or chat' },
        { value: 'createChannel', name: 'Create Channel', description: 'Create a new channel in a team' },
        { value: 'readMessages', name: 'Read Messages', description: 'Read messages from a channel' },
        { value: 'postAdaptiveCard', name: 'Post Adaptive Card', description: 'Post an Adaptive Card to a channel' },
        { value: 'manageMembers', name: 'Manage Members', description: 'Add or remove team members' },
        { value: 'createTeam', name: 'Create Team', description: 'Create a new team' },
        { value: 'listTeams', name: 'List Teams', description: 'List all teams the user is a member of' },
        { value: 'listChannels', name: 'List Channels', description: 'List all channels in a team' },
        { value: 'uploadFile', name: 'Upload File', description: 'Upload a file to a channel' },
        { value: 'scheduleMeeting', name: 'Schedule Meeting', description: 'Schedule a Teams meeting' }
      ]
    },

    // Send Message properties
    {
      key: 'messageType',
      label: 'Message Type',
      type: 'options',
      default: 'channel',
      required: true,
      displayOptions: {
        show: {
          operation: ['sendMessage']
        }
      },
      options: [
        { value: 'channel', name: 'Channel Message' },
        { value: 'chat', name: 'Chat Message' }
      ]
    },
    {
      key: 'teamId',
      label: 'Team',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['sendMessage', 'createChannel', 'readMessages', 'postAdaptiveCard', 'manageMembers', 'listChannels', 'uploadFile'],
          messageType: ['channel']
        }
      },
      description: 'The team to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTeams',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'channelId',
      label: 'Channel',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['sendMessage', 'readMessages', 'postAdaptiveCard', 'uploadFile'],
          messageType: ['channel']
        }
      },
      description: 'The channel to use',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getChannels',
          dependsOn: ['teamId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'chatId',
      label: 'Chat',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['sendMessage'],
          messageType: ['chat']
        }
      },
      description: 'The chat to send the message to',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getChats',
          cacheResults: true,
          cacheTtl: 30000,
          preserveOrder: true, // Keep most recent chats first (API order)
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'message',
      label: 'Message',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['sendMessage']
        }
      },
      description: 'The message content'
    },

    // Attachment for Send Message
    {
      key: 'attachmentContent',
      label: 'Attachment',
      type: 'text',
      default: '',
      customComponent: 'FileUpload',
      displayOptions: {
        show: {
          operation: ['sendMessage']
        }
      },
      description: 'Upload a file to attach to the message. Use {{ }} expressions to reference upstream node output.'
    },
    {
      key: 'fileName',
      label: 'Attachment File Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          operation: ['sendMessage']
        }
      },
      description: 'Name of the attachment file. Auto-populated from file upload, or use {{ }} expressions e.g. {{ $node["Download"].output.fileName }}'
    },
    {
      key: 'mimeType',
      label: 'Attachment MIME Type',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          operation: ['sendMessage']
        }
      },
      description: 'MIME type of the attachment. Auto-populated from file upload, or use {{ }} expressions. Leave empty to auto-detect from file name.'
    },

    // Create Channel properties
    {
      key: 'channelName',
      label: 'Channel Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['createChannel']
        }
      },
      description: 'The name of the new channel'
    },
    {
      key: 'channelDescription',
      label: 'Channel Description',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          operation: ['createChannel']
        }
      },
      description: 'Optional description for the channel'
    },

    // Read Messages properties
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          operation: ['readMessages']
        }
      },
      description: 'Maximum number of messages to retrieve'
    },

    // Post Adaptive Card properties
    {
      key: 'adaptiveCardJson',
      label: 'Adaptive Card JSON',
      type: 'json',
      required: true,
      displayOptions: {
        show: {
          operation: ['postAdaptiveCard']
        }
      },
      description: 'The Adaptive Card JSON definition'
    },

    // Manage Members properties
    {
      key: 'memberAction',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'add',
      displayOptions: {
        show: {
          operation: ['manageMembers']
        }
      },
      options: [
        { value: 'add', name: 'Add Member' },
        { value: 'remove', name: 'Remove Member' }
      ]
    },
    {
      key: 'userEmail',
      label: 'User Email',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['manageMembers']
        }
      },
      description: 'The email address of the user to add/remove'
    },

    // Create Team properties
    {
      key: 'teamName',
      label: 'Team Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['createTeam']
        }
      },
      description: 'The name of the new team'
    },
    {
      key: 'teamDescription',
      label: 'Team Description',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          operation: ['createTeam']
        }
      },
      description: 'Optional description for the team'
    },

    // Upload File properties
    {
      key: 'fileName',
      label: 'File Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['uploadFile']
        }
      },
      description: 'The name of the file to upload'
    },
    {
      key: 'fileContent',
      label: 'File Content',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['uploadFile']
        }
      },
      description: 'The content of the file'
    },

    // Schedule Meeting properties
    {
      key: 'subject',
      label: 'Meeting Subject',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['scheduleMeeting']
        }
      },
      description: 'The subject of the meeting'
    },
    {
      key: 'startTime',
      label: 'Start Time',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['scheduleMeeting']
        }
      },
      description: 'Meeting start time (ISO 8601 format, e.g., 2024-01-15T10:00:00)'
    },
    {
      key: 'endTime',
      label: 'End Time',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          operation: ['scheduleMeeting']
        }
      },
      description: 'Meeting end time (ISO 8601 format, e.g., 2024-01-15T11:00:00)'
    },
    {
      key: 'attendees',
      label: 'Attendees',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          operation: ['scheduleMeeting']
        }
      },
      description: 'Comma-separated list of attendee email addresses'
    }
  ]
};

export default schema;
